import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

import type { EventTrigger, Media, MediaType, TimestampInMillis, UUID } from '@cobliteam/common-types';

import { GrahpQLTrackingRequester, type TrackingGraphQL, type TrackingSpeedEvents } from '@cobliteam/api-lib';
import type { VehicleTrackingSegmentType } from '@cobliteam/api-lib/src/__generated__/graphql';

const graphQLTrackingRequester = new GrahpQLTrackingRequester();

const QUERY_KEY = 'tracking';

const useTracking = () => {
  const getTrackingEssencialInfo = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean, enabled = false) => {
    return useQuery<TrackingGraphQL>({
      queryKey: [`${QUERY_KEY}-stops`, vehicleId, startTimestamp, endTimestamp, useMapMatch],
      queryFn: () => graphQLTrackingRequester.getTrackingEssencialInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch),
      refetchOnMount: false,
      enabled,
    });
  };

  const getTrackingSpeedEvents = (vehicleId: UUID, startTimestamp: TimestampInMillis, endTimestamp: TimestampInMillis, useMapMatch: boolean) => {
    return useQuery<TrackingSpeedEvents>({
      queryKey: [`${QUERY_KEY}-speed-events`, vehicleId, startTimestamp, endTimestamp, useMapMatch],
      queryFn: () => graphQLTrackingRequester.getTrackingSpeedEvents(vehicleId, startTimestamp, endTimestamp, useMapMatch),
      refetchOnMount: false,
      select(data) {
        return {
          ...data,
          roadSpeedEvents: data.roadSpeedEvents.map((event) => ({ ...event, type: 'road' as const })),
          vehicleSpeedEvents: data.vehicleSpeedEvents.map((event) => ({ ...event, type: 'vehicle' as const })),
        };
      },
    });
  };

  const getTrackingSegment = (
    vehicleId: UUID,
    startTime: TimestampInMillis,
    endTime: TimestampInMillis,
    segmentStart: TimestampInMillis,
    segmentEnd: TimestampInMillis,
    allowUnfinishedStep: boolean,
    match: boolean,
    eventTriggers?: EventTrigger[],
    mediaTypes?: MediaType[],
    enabled = true
  ) => {
    return useQuery<Omit<VehicleTrackingSegmentType, 'mediaEvents'> & { mediaEvents: Media[] }>({
      queryKey: [`${QUERY_KEY}-route-segment`, vehicleId, segmentStart, segmentEnd],
      queryFn: () =>
        graphQLTrackingRequester.getVehicleTrackingSegment(vehicleId, startTime, endTime, segmentStart, segmentEnd, allowUnfinishedStep, match, eventTriggers, mediaTypes),
      refetchOnMount: false,
      enabled,
    });
  };

  return {
    getTrackingEssencialInfo,
    getTrackingSpeedEvents,
    getTrackingSegment,
  };
};

const useGetTracking = (
  vehicleId: UUID,
  startTimestamp: TimestampInMillis,
  endTimestamp: TimestampInMillis,
  useMapMatch: boolean,
  options?: Partial<UseQueryOptions<TrackingGraphQL>>
) => {
  return useQuery<TrackingGraphQL>({
    queryKey: [QUERY_KEY, vehicleId, startTimestamp, endTimestamp, useMapMatch],
    queryFn: () => graphQLTrackingRequester.getFullTrackingInfo(vehicleId, startTimestamp, endTimestamp, useMapMatch),
    ...options,
  });
};

export default useTracking;
export { useGetTracking };
